import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext, useMemo } from 'react';
import { MaintenanceTask, MaintenanceTaskData, MaintenanceTaskImpl } from '../types';
import { Timestamp, where } from 'firebase/firestore';
import { auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { useYearContext } from './YearContext';
import { useSyncSignal } from './SheetSyncContext';
import {
    fetchCollection,
    addToCollection,
    updateInCollection,
    deleteFromCollection,
    COLLECTIONS
} from '../firebase/firestoreUtils';

interface MaintenanceTasksContextType {
    tasks: MaintenanceTask[];
    isLoading: boolean;
    addTask: (task: Omit<MaintenanceTaskData, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    updateTask: (task: MaintenanceTask) => Promise<void>;
    deleteTask: (taskId: string) => Promise<void>;
}

export const MaintenanceTasksContext = createContext<MaintenanceTasksContextType | undefined>(undefined);

export const MaintenanceTasksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { selectedYear } = useYearContext();
    // Refetch when a Google-Sheets sync run lands (see SheetSyncContext).
    const syncSignal = useSyncSignal();
    const [tasks, setTasks] = useState<MaintenanceTask[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchTasks = async (isAuthenticated: boolean) => {
            setIsLoading(true);
            try {
                if (isAuthenticated) {
                    const data = await fetchCollection<MaintenanceTaskData>(
                        COLLECTIONS.MAINTENANCE_TASKS,
                        [where('year', '==', selectedYear)]
                    );
                    setTasks(data.map(t => new MaintenanceTaskImpl(t)));
                } else {
                    setTasks([]);
                }
            } catch (error) {
                console.error('Failed to fetch maintenance tasks from Firestore', error);
            } finally {
                setIsLoading(false);
            }
        };

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            fetchTasks(!!user);
        });

        return () => unsubscribe();
    }, [selectedYear, syncSignal]);

    const refetch = useCallback(async () => {
        const updated = await fetchCollection<MaintenanceTaskData>(
            COLLECTIONS.MAINTENANCE_TASKS,
            [where('year', '==', selectedYear)]
        );
        setTasks(updated.map(t => new MaintenanceTaskImpl(t)));
    }, [selectedYear]);

    const addTask = useCallback(async (taskData: Omit<MaintenanceTaskData, 'id' | 'createdAt' | 'updatedAt'>) => {
        try {
            const dataWithTimestamp = {
                ...taskData,
                year: taskData.year || selectedYear,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };
            await addToCollection<MaintenanceTaskData>(COLLECTIONS.MAINTENANCE_TASKS, dataWithTimestamp);
            await refetch();
        } catch (error) {
            console.error("Error adding maintenance task: ", error);
            throw error;
        }
    }, [selectedYear, refetch]);

    const updateTask = useCallback(async (taskData: MaintenanceTask) => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, createdAt, updatedAt, ...restData } = taskData;
            const dataToUpdate = {
                ...restData,
                updatedAt: Timestamp.now()
            };
            await updateInCollection<MaintenanceTaskData>(COLLECTIONS.MAINTENANCE_TASKS, String(taskData.id), dataToUpdate);
            await refetch();
        } catch (error) {
            console.error("Error updating maintenance task: ", error);
            throw error;
        }
    }, [refetch]);

    const deleteTask = useCallback(async (taskId: string) => {
        try {
            await deleteFromCollection(COLLECTIONS.MAINTENANCE_TASKS, taskId);
            await refetch();
        } catch (error) {
            console.error("Error deleting maintenance task: ", error);
            throw error;
        }
    }, [refetch]);

    const value = useMemo(() => ({
        tasks,
        isLoading,
        addTask,
        updateTask,
        deleteTask,
    }), [
        tasks,
        isLoading,
        addTask,
        updateTask,
        deleteTask,
    ]);

    return (
        <MaintenanceTasksContext.Provider value={value}>
            {children}
        </MaintenanceTasksContext.Provider>
    );
};

export const useMaintenanceTasks = () => {
    const context = useContext(MaintenanceTasksContext);
    if (!context) {
        throw new Error('useMaintenanceTasks must be used within a MaintenanceTasksProvider');
    }
    return context;
};
